<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div class="d-flex align-items-center gap-3">
                <router-link
                  :to="{ name: 'Remissions' }"
                  class="btn btn-outline-secondary btn-sm"
                >
                  <i class="fas fa-arrow-left me-1"></i> Volver
                </router-link>
                <h6 class="mb-0">Picking / Packing — Remisión #{{ remissionId }}</h6>
              </div>
              <div class="d-flex gap-2">
                <button
                  type="button"
                  class="btn btn-success btn-sm"
                  @click="showPickingModal = true"
                >
                  <i class="fas fa-box me-1"></i> Iniciar Picking / Packing
                </button>
                <button
                  type="button"
                  class="btn btn-sm"
                  style="background-color: #C2185B; border-color: #C2185B; color: #fff;"
                  :disabled="downloadingNote"
                  @click="downloadNote"
                >
                  <i class="fas" :class="downloadingNote ? 'fa-spinner fa-spin' : 'fa-file-pdf'"></i>
                  PDF pedido
                </button>
                <button
                  type="button"
                  class="btn btn-sm"
                  style="background-color: #6f7a93; border-color: #6f7a93; color: #fff;"
                  :disabled="downloadingNoStock"
                  @click="downloadNoStockNote"
                >
                  <i class="fas" :class="downloadingNoStock ? 'fa-spinner fa-spin' : 'fa-file-pdf'"></i>
                  PDF prod.
                </button>
              </div>
            </div>

            <div v-if="loading" class="text-center py-5">
              <i class="fas fa-spinner fa-spin fa-2x text-success"></i>
              <p class="mt-2 text-sm text-secondary">Cargando detalles...</p>
            </div>

            <div v-else-if="remission" class="mb-3">
              <div class="row">
                <div class="col-md-3">
                  <label class="form-control-label text-xs">Cliente</label>
                  <p class="text-sm font-weight-bold mb-0">{{ remission.name }}</p>
                </div>
                <div class="col-md-3">
                  <label class="form-control-label text-xs">Teléfono</label>
                  <p class="text-sm mb-0">{{ remission.phone }}</p>
                </div>
                <div class="col-md-3">
                  <label class="form-control-label text-xs">Estado</label><br>
                  <span class="badge badge-sm" :class="remission.type === 'return' ? 'bg-danger' : (remission.status == 1 ? 'bg-success' : 'bg-warning')">
                    {{ remission.type === 'return' ? 'Devolución' : (remission.status == 1 ? 'Formalizada' : 'Pendiente') }}
                  </span>
                  <span v-if="remission.preparado == 1" class="badge badge-sm bg-info text-dark ms-1">
                    <i class="fas fa-check-circle"></i> Preparado
                  </span>
                  <span v-else-if="remission.preparado == 2" class="badge badge-sm bg-success ms-1">
                    <i class="fas fa-truck"></i> Enviado
                  </span>
                </div>
                <div class="col-md-3">
                  <label class="form-control-label text-xs">Fecha</label>
                  <p class="text-sm mb-0">{{ new Date(remission.created_at).toLocaleDateString('es-ES') }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="card-body px-0 pt-0 pb-2">
            <div v-if="!loading && details.length > 0" class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Prep.</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Estado</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Producto</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Ref</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Talla</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Color</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Código de barras</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Cantidad</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Precio</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in details" :key="item.id">
                    <td class="text-center align-middle">
                      <i v-if="item.prepared" class="fas fa-check-circle text-success" title="Preparado"></i>
                      <i v-else class="fas fa-clock text-warning" title="No preparado"></i>
                    </td>
                    <td class="text-center align-middle">
                      <i v-if="item.prepared" class="fas fa-check-circle text-success" title="Preparado"></i>
                      <template v-else-if="itemStock(item) < item.quantity">
                        <span v-if="itemManufacturingStatus(item) === 'completed'" class="status-badge status-badge--completed">
                          <i class="fas fa-check-circle"></i> Terminado
                        </span>
                        <select
                          v-else
                          :value="itemManufacturingStatus(item)"
                          class="status-select"
                          :class="statusPillClass(item)"
                          @change="onStatusChange($event, item)"
                        >
                          <option value="pending">Pendiente</option>
                          <option value="in_progress">En proceso</option>
                          <option value="completed">Terminado</option>
                        </select>
                      </template>
                      <i v-else class="fas fa-check-circle text-success" title="Stock suficiente"></i>
                    </td>
                    <td class="align-middle">
                      <h6 class="mb-0 text-sm">{{ item.inventory?.name || 'Producto' }}</h6>
                    </td>
                    <td class="text-center align-middle">
                      <span class="text-xs text-secondary">{{ item.inventory?.ref || '—' }}</span>
                    </td>
                    <td class="text-center align-middle">
                      <span class="text-xs text-secondary">{{ itemSize(item) }}</span>
                    </td>
                    <td class="text-center align-middle">
                      <span class="text-xs text-secondary">{{ itemColor(item) }}</span>
                    </td>
                    <td class="text-center align-middle">
                      <span class="text-xs text-secondary">{{ itemBarcode(item) }}</span>
                    </td>
                    <td class="text-center align-middle">
                      <span class="text-sm font-weight-bold">{{ item.quantity }}</span> / 
                      <span class="badge badge-sm" :class="itemStock(item) <= 0 ? 'bg-danger' : (itemStock(item) < item.quantity ? 'bg-warning text-dark' : 'bg-success')">
                        {{ itemStock(item) }}
                      </span>
                    </td>
                    <td class="text-center align-middle">
                      <span class="text-xs text-secondary">{{ parseFloat(item.unit_price || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</span>
                    </td>
                    <td class="text-center align-middle">
                      <span class="text-xs font-weight-bold">{{ parseFloat(item.total || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div class="px-4 py-3 d-flex gap-2">
                <span class="badge bg-secondary">Pendientes: {{ statusCount('pending') }}</span>
                <span class="badge bg-info text-dark">En proceso: {{ statusCount('in_progress') }}</span>
                <span class="badge bg-success">Terminados: {{ statusCount('completed') }}</span>
              </div>
            </div>

            <div v-else-if="!loading" class="text-center py-4">
              <p class="text-secondary text-sm">No hay detalles para esta remisión.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Picking / Packing -->
    <div v-if="showPickingModal" class="picking-modal-overlay" @click.self="showPickingModal = false">
      <div class="picking-modal-box card shadow">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0">Escanear producto — Remisión #{{ remissionId }}</h6>
            <button type="button" class="btn-close" @click="showPickingModal = false"></button>
          </div>

          <div class="picking-summary mb-3">
            <span class="badge bg-success">Preparados: {{ preparedCount }}</span>
            <span class="badge bg-warning text-dark">No preparados: {{ details.length - preparedCount }}</span>
          </div>

          <label class="form-control-label text-sm">Escanear código de barras</label>
          <div class="d-flex gap-2 mb-3">
            <input
              ref="barcodeInput"
              v-model="barcodeScan"
              type="text"
              class="form-control form-control-sm"
              placeholder="Escanee o digite el código de barras..."
              @input="onBarcodeInput"
              @keyup.enter="onBarcodeScan"
            />
            <button type="button" class="btn btn-success btn-sm d-flex align-items-center justify-content-center" @click="onBarcodeScan" :disabled="!barcodeScan.trim()" style="margin-bottom: 0; flex-shrink: 0;">
              <i class="fas fa-check"></i>
            </button>
          </div>
          <div v-if="scanMessage" class="mt-2 mb-3" :class="scanError ? 'text-danger' : 'text-success'">
            <small>{{ scanMessage }}</small>
          </div>

          <div class="picking-items-list">
            <div
              v-for="item in sortedDetails"
              :key="item.id"
              class="picking-item-row"
              :class="{ 'picking-item-row--prepared': item.prepared }"
            >
              <div class="picking-item-status">
                <i v-if="item.prepared" class="fas fa-check-circle text-success"></i>
                <i v-else class="fas fa-clock text-warning"></i>
              </div>
              <div class="picking-item-info">
                <div class="picking-item-name">{{ item.inventory?.name || 'Producto' }}</div>
                <div class="picking-item-meta">
                  Ref: {{ item.inventory?.ref || '—' }} ·
                  Talla: {{ itemSize(item) }} ·
                  Color: {{ itemColor(item) }} ·
                  Cant: {{ item.quantity }} ·
                  Barcode: {{ itemBarcode(item) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import remissionsService from '@/services/remissions.service';
import remissionsDetailsService from '@/services/remissionsDetails.service';

export default {
  name: 'PickingPacking',
  data() {
    return {
      remissionId: null,
      remission: null,
      details: [],
      loading: true,
      showPickingModal: false,
      barcodeScan: '',
      scanMessage: '',
      scanError: false,
      downloadingNote: false,
      downloadingNoStock: false,
    };
  },
  computed: {
    preparedCount() {
      return this.details.filter(d => d.prepared).length;
    },
    sortedDetails() {
      return [...this.details].sort((a, b) => {
        if (a.prepared === b.prepared) return 0;
        return a.prepared ? 1 : -1;
      });
    },
  },
  methods: {
    itemManufacturingStatus(item) {
      return item.manufacturing_status || 'pending';
    },
    statusLabel(item) {
      const s = this.itemManufacturingStatus(item);
      if (s === 'completed') return 'Terminado';
      if (s === 'in_progress') return 'En proceso';
      return 'Pendiente';
    },
    statusPillClass(item) {
      const s = this.itemManufacturingStatus(item);
      if (s === 'completed') return 'status-pill--completed';
      if (s === 'in_progress') return 'status-pill--in-progress';
      return 'status-pill--pending';
    },
    statusIconClass(item) {
      const s = this.itemManufacturingStatus(item);
      if (s === 'completed') return 'fas fa-check-circle';
      if (s === 'in_progress') return 'fas fa-cog fa-spin';
      return 'fas fa-clock';
    },
    statusCount(status) {
      return this.details.filter(d => this.itemManufacturingStatus(d) === status).length;
    },
    revertStatus(target, item, current) {
      target.value = current;
      item.manufacturing_status = current;
    },
    async onStatusChange(event, item) {
      const newStatus = event.target.value;
      const current = this.itemManufacturingStatus(item);
      const target = event.target;

      if (newStatus === current) return;

      if (newStatus === 'completed') {
        const result = await Swal.fire({
          title: 'Confirmar terminado',
          text: `¿Cambiar el estado de "${item.inventory?.name || 'Producto'}" a Terminado? Se sumarán ${item.quantity} unidades al inventario.`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sí, terminar',
          cancelButtonText: 'Cancelar',
          customClass: {
            container: 'swal-high-z',
            popup: 'swal-status-popup',
            confirmButton: 'swal-status-confirm',
            cancelButton: 'swal-status-cancel',
          },
        });

        if (!result.isConfirmed) {
          this.revertStatus(target, item, current);
          return;
        }
      }

      try {
        const res = await remissionsDetailsService.updateManufacturingStatus(item.id, newStatus);
        const updated = res.data.detail;
        const idx = this.details.findIndex(d => d.id === item.id);
        if (idx >= 0) {
          this.details[idx] = {
            ...this.details[idx],
            manufacturing_status: updated.manufacturing_status,
            manufactured: updated.manufactured,
            inventory: updated.inventory,
            variant: updated.variant,
          };
        }
        await Swal.fire({
          icon: 'success',
          title: 'Estado actualizado',
          text: res.data.message,
          timer: 1500,
          showConfirmButton: false,
          customClass: { container: 'swal-high-z' },
        });
      } catch (error) {
        console.error('Error al actualizar estado:', error);
        this.revertStatus(target, item, current);
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'No se pudo actualizar el estado',
          customClass: { container: 'swal-high-z' },
        });
      }
    },
    itemSize(item) {
      if (item.variant?.size?.size) return item.variant.size.size;
      if (item.productionModel?.size?.size) return item.productionModel.size.size;
      return '—';
    },
    itemColor(item) {
      if (item.variant?.color?.name) return item.variant.color.name;
      if (item.productionModel?.size?.color?.name) return item.productionModel.size.color.name;
      if (item.inventory?.color?.name) return item.inventory.color.name;
      return '—';
    },
    itemStock(item) {
      if (item.variant) {
        return item.variant.quantity ?? 0;
      }
      return item.inventory?.quantity ?? 0;
    },
    itemBarcode(item) {
      if (item.variant && item.variant.barcode) return item.variant.barcode;
      return item.inventory?.barcode || '—';
    },
    async downloadNoStockNote() {
      this.downloadingNoStock = true;
      try {
        const response = await remissionsService.getNoStockNote(this.remissionId);
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `nota_sin_stock_${this.remissionId}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al generar el PDF sin stock:', error);
        await Swal.fire('Error', 'No se pudo generar el PDF sin stock', 'error');
      } finally {
        this.downloadingNoStock = false;
      }
    },
    async loadData() {
      this.remissionId = this.$route.query.id;
      if (!this.remissionId) {
        this.loading = false;
        return;
      }
      try {
        const [remRes, detRes] = await Promise.all([
          remissionsService.getData(this.remissionId),
          remissionsDetailsService.getData(this.remissionId),
        ]);
        this.remission = remRes.data;
        this.details = (detRes.data || []).map(d => ({
          ...d,
          prepared: !!d.prepared,
        }));
      } catch (error) {
        console.error('Error al cargar datos:', error);
        await Swal.fire('Error', 'No se pudieron cargar los datos de la remisión', 'error');
      } finally {
        this.loading = false;
      }
    },
    onBarcodeInput() {
      clearTimeout(this._barcodeTimer);
      const code = this.barcodeScan.trim();
      if (!code) return;
      this._barcodeTimer = setTimeout(() => {
        const match = this.details.find(d => {
          if (d.variant && d.variant.barcode) {
            return d.variant.barcode === code;
          }
          const inv = d.inventory;
          if (!inv) return false;
          if (inv.barcode === code) return true;
          const variants = inv.shopify_details?.variants || [];
          return variants.some(v => v.barcode === code);
        });
        if (match) {
          this.onBarcodeScan();
        }
      }, 300);
    },
    async downloadNote() {
      this.downloadingNote = true;
      try {
        const response = await remissionsService.getNote(this.remissionId);
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `nota_envio_${this.remissionId}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al generar el PDF:', error);
        await Swal.fire('Error', 'No se pudo generar el PDF', 'error');
      } finally {
        this.downloadingNote = false;
      }
    },
    async onBarcodeScan() {
      const code = this.barcodeScan.trim();
      if (!code) return;
      this.scanMessage = '';
      this.scanError = false;
      try {
        const res = await remissionsService.markPrepared(this.remissionId, code);
        const detailId = res.data.detail?.id;
        if (detailId) {
          const item = this.details.find(d => d.id === detailId);
          if (item) item.prepared = true;
        }
        this.scanMessage = res.data.message || 'Producto marcado como preparado';

        if (res.data.remission_preparado) {
          this.remission = { ...this.remission, preparado: true };
          await Swal.fire({
            icon: 'success',
            title: '¡Pedido preparado!',
            text: 'Todos los productos han sido marcados como preparados.',
            timer: 2000,
            showConfirmButton: false,
            customClass: { container: 'swal-high-z' },
          });
        }

        this.barcodeScan = '';
        this.$nextTick(() => {
          if (this.$refs.barcodeInput) this.$refs.barcodeInput.focus();
        });
      } catch (error) {
        this.scanError = true;
        this.scanMessage = error.response?.data?.message || 'Error al marcar el producto';
        if (error.response?.status === 422) {
          await Swal.fire({
            icon: 'warning',
            title: 'Stock insuficiente',
            text: error.response?.data?.message || 'No hay stock suficiente',
            customClass: {
              container: 'swal-high-z',
            },
          });
        }
        this.barcodeScan = '';
        this.$nextTick(() => {
          if (this.$refs.barcodeInput) this.$refs.barcodeInput.focus();
        });
      }
    },
  },
  watch: {
    showPickingModal(val) {
      if (val) {
        this.barcodeScan = '';
        this.scanMessage = '';
        this.scanError = false;
        this.$nextTick(() => {
          if (this.$refs.barcodeInput) this.$refs.barcodeInput.focus();
        });
      }
    },
  },
  async mounted() {
    await this.loadData();
  },
};
</script>

<style scoped>
.picking-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.picking-modal-box {
  width: 600px;
  max-width: 95vw;
  max-height: 85vh;
  overflow-y: auto;
}

.picking-summary {
  display: flex;
  gap: 0.5rem;
}

.picking-items-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

.picking-item-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.75rem;
  border-bottom: 1px solid #f1f1f1;
}

.picking-item-row:last-child {
  border-bottom: none;
}

.picking-item-row--prepared {
  background: #f0fff4;
}

.picking-item-status {
  font-size: 1.1rem;
  flex-shrink: 0;
}

.picking-item-info {
  flex: 1;
  min-width: 0;
}

.picking-item-name {
  font-weight: 600;
  font-size: 0.875rem;
}

.picking-item-meta {
  font-size: 0.75rem;
  color: #6c757d;
}

.status-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.3rem 1.8rem 0.3rem 0.8rem;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s ease;
  line-height: 1.2;
  outline: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='currentColor' viewBox='0 0 16 16'%3e%3cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.55rem center;
  background-size: 10px;
}

.status-select:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.status-select:focus {
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.status-select option {
  font-size: 0.85rem;
  padding: 0.3rem 0.5rem;
  border-radius: 0;
}

.status-pill--pending {
  background: #f3f4f6;
  color: #6b7280;
}

.status-pill--pending:hover {
  background: #e5e7eb;
}

.status-pill--in-progress {
  background: #fef3c7;
  color: #b45309;
}

.status-pill--in-progress:hover {
  background: #fde68a;
}

.status-pill--completed {
  background: #d1fae5;
  color: #047857;
}

.status-pill--completed:hover {
  background: #a7f3d0;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 0.8; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(1.3); }
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.3rem 0.8rem;
  border-radius: 999px;
  white-space: nowrap;
  line-height: 1.2;
}

.status-badge--completed {
  background: #d1fae5;
  color: #047857;
}
</style>

<style>
.swal-high-z {
  z-index: 100000 !important;
}

.swal-status-popup {
  border-radius: 16px !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15) !important;
}

.swal-status-select {
  border: 2px solid #e2e8f0 !important;
  border-radius: 10px !important;
  padding: 0.7rem 2.5rem 0.7rem 1rem !important;
  font-size: 0.95rem !important;
  font-weight: 600 !important;
  color: #334155 !important;
  background-color: #f8fafc !important;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%2364748b' viewBox='0 0 16 16'%3e%3cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3e%3c/svg%3e") !important;
  background-repeat: no-repeat !important;
  background-position: right 0.85rem center !important;
  background-size: 14px !important;
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
  margin-top: 0.75rem !important;
}

.swal-status-select:focus {
  border-color: #6366f1 !important;
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.12) !important;
  outline: none !important;
  background-color: #fff !important;
}

.swal-status-select:hover {
  border-color: #cbd5e1 !important;
  background-color: #f1f5f9 !important;
}

.swal-status-confirm {
  background-color: #6366f1 !important;
  border: none !important;
  border-radius: 10px !important;
  font-weight: 600 !important;
  padding: 0.6rem 1.5rem !important;
  font-size: 0.9rem !important;
  transition: all 0.2s ease !important;
}

.swal-status-confirm:hover {
  background-color: #4f46e5 !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3) !important;
}

.swal-status-cancel {
  border-radius: 10px !important;
  font-weight: 600 !important;
  padding: 0.6rem 1.5rem !important;
  font-size: 0.9rem !important;
  transition: all 0.2s ease !important;
}

.swal-status-cancel:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}
</style>
